import React from 'react'

import GradientButton from '../../components/GradientButton'
import { useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import type { CapacityBand, CarryingCapacityTable } from '../../utils/carryingCapacity'
import { UNIT_TYPES, type UnitType } from '../../utils/density'

// Capacidad de carga editor (findings #151): one table's kg/m³ limits per
// weight band × unit type, its minimum flow (L/min per kg) per weight band ×
// temperature band, and the hatchery minimum per 1,000. Empty cell = no limit.
// Replaces the #96 grid that lived in Editar instalación.

export const UNIT_TYPE_LABEL_KEY: Record<UnitType, TranslationKey> = {
  floatingCage: 'density.unitType.floatingCage',
  circularTank: 'density.unitType.circularTank',
  raceway: 'density.unitType.raceway',
  pond: 'density.unitType.pond',
  incubator: 'density.unitType.incubator',
  fibreglassTank: 'density.unitType.fibreglassTank'
}

export type DraftBand = { maxWeightG: string; limits: Record<UnitType, string>; flow: string[] }
export type CapacityDraft = { name: string; bands: DraftBand[]; temperatures: string[]; hatcheryMin: string }

const text = (value: number | null | undefined): string => (value === null || value === undefined ? '' : String(value).replace('.', ','))

const emptyLimits = (): Record<UnitType, string> =>
  Object.fromEntries(UNIT_TYPES.map((type) => [type, ''])) as Record<UnitType, string>

export const toCapacityDraft = (table: Pick<CarryingCapacityTable, 'name' | 'bands' | 'flowTemperaturesC' | 'hatcheryMinLpmPerThousand'>): CapacityDraft => {
  const columns = table.flowTemperaturesC.length + 1
  const toBand = (band: CapacityBand, open: boolean): DraftBand => ({
    maxWeightG: open ? '' : text(band.maxWeightG),
    limits: Object.fromEntries(UNIT_TYPES.map((type) => [type, text(band.limits[type])])) as Record<UnitType, string>,
    flow: Array.from({ length: columns }, (_, index) => text(band.minFlowLpmPerKg[index]))
  })
  const closed = table.bands.filter((band) => band.maxWeightG !== null)
  const open = table.bands.find((band) => band.maxWeightG === null)
  return {
    name: table.name,
    bands: [
      ...closed.map((band) => toBand(band, false)),
      open ? toBand(open, true) : { maxWeightG: '', limits: emptyLimits(), flow: Array.from({ length: columns }, () => '') }
    ],
    temperatures: table.flowTemperaturesC.map((value) => text(value)),
    hatcheryMin: text(table.hatcheryMinLpmPerThousand)
  }
}

const parseNumber = (value: string, allowNegative = false): number | null | 'invalid' => {
  const trimmed = value.trim().replace(',', '.')
  if (!trimmed) return null
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return 'invalid'
  if (!allowNegative && parsed <= 0) return 'invalid'
  return parsed
}

export type ParsedCapacityDraft = Pick<CarryingCapacityTable, 'name' | 'bands' | 'flowTemperaturesC' | 'hatcheryMinLpmPerThousand'>

/** Draft → table fields, or an error key. Weight and temperature bands must increase. */
export const parseCapacityDraft = (draft: CapacityDraft): { ok: true; value: ParsedCapacityDraft } | { ok: false; errorKey: TranslationKey } => {
  const name = draft.name.trim()
  if (!name) return { ok: false, errorKey: 'capacity.error.name' }
  const temperatures: number[] = []
  for (const raw of draft.temperatures) {
    const parsed = parseNumber(raw, true)
    if (parsed === null || parsed === 'invalid' || (temperatures.length > 0 && parsed <= temperatures[temperatures.length - 1])) {
      return { ok: false, errorKey: 'capacity.error.temperatures' }
    }
    temperatures.push(parsed)
  }
  const bands: CapacityBand[] = []
  let previous = 0
  for (const [index, row] of draft.bands.entries()) {
    const open = index === draft.bands.length - 1
    const limits: Partial<Record<UnitType, number>> = {}
    for (const type of UNIT_TYPES) {
      const parsed = parseNumber(row.limits[type])
      if (parsed === 'invalid') return { ok: false, errorKey: 'capacity.error.values' }
      if (parsed !== null) limits[type] = parsed
    }
    const minFlowLpmPerKg: Array<number | null> = []
    for (let column = 0; column <= temperatures.length; column += 1) {
      const parsed = parseNumber(row.flow[column] ?? '')
      if (parsed === 'invalid') return { ok: false, errorKey: 'capacity.error.values' }
      minFlowLpmPerKg.push(parsed)
    }
    let maxWeightG: number | null = null
    if (!open) {
      const weight = parseNumber(row.maxWeightG)
      if (weight === null || weight === 'invalid' || weight <= previous) return { ok: false, errorKey: 'capacity.error.weights' }
      previous = weight
      maxWeightG = weight
    }
    bands.push({ maxWeightG, limits, minFlowLpmPerKg })
  }
  const hatchery = parseNumber(draft.hatcheryMin)
  if (hatchery === 'invalid') return { ok: false, errorKey: 'capacity.error.values' }
  return { ok: true, value: { name, bands, flowTemperaturesC: temperatures, hatcheryMinLpmPerThousand: hatchery } }
}

export type CapacityTableEditorProps = {
  draft: CapacityDraft
  onChange: (update: (current: CapacityDraft) => CapacityDraft) => void
  disabled: boolean
}

const inputStyle: React.CSSProperties = { padding: '6px 8px', border: '1px solid #cbd5f5', borderRadius: 8, width: 72 }
const cellStyle: React.CSSProperties = { padding: '4px 8px', whiteSpace: 'nowrap' }

export default function CapacityTableEditor({ draft, onChange, disabled }: CapacityTableEditorProps) {
  const t = useT()
  const columns = draft.temperatures.length + 1

  const weightLabel = (index: number): React.ReactNode => {
    const open = index === draft.bands.length - 1
    if (!open) return null
    if (draft.bands.length === 1) return t('editFarm.density.allWeights')
    return t('editFarm.density.moreThan', { weight: draft.bands[index - 1].maxWeightG.trim() || '…' })
  }

  const setBand = (index: number, update: (band: DraftBand) => DraftBand) => {
    onChange((current) => ({ ...current, bands: current.bands.map((band, i) => (i === index ? update(band) : band)) }))
  }

  const addBand = () => {
    onChange((current) => ({
      ...current,
      bands: [
        ...current.bands.slice(0, -1),
        { maxWeightG: '', limits: emptyLimits(), flow: Array.from({ length: current.temperatures.length + 1 }, () => '') },
        current.bands[current.bands.length - 1]
      ]
    }))
  }

  const removeBand = (index: number) => {
    onChange((current) => ({ ...current, bands: current.bands.filter((_, i) => i !== index) }))
  }

  // A new temperature band splits the open (warmest) column: its values are copied.
  const addTemperature = () => {
    onChange((current) => ({
      ...current,
      temperatures: [...current.temperatures, ''],
      bands: current.bands.map((band) => ({ ...band, flow: [...band.flow.slice(0, current.temperatures.length), band.flow[current.temperatures.length] ?? '', band.flow[current.temperatures.length] ?? ''] }))
    }))
  }

  const removeTemperature = (column: number) => {
    onChange((current) => ({
      ...current,
      temperatures: current.temperatures.filter((_, i) => i !== column),
      bands: current.bands.map((band) => ({ ...band, flow: band.flow.filter((_, i) => i !== column) }))
    }))
  }

  const weightCell = (index: number) => {
    const open = index === draft.bands.length - 1
    return open ? weightLabel(index) : (
      <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        {t('editFarm.density.upTo')}
        <input
          aria-label={t('editFarm.density.upTo')}
          data-testid={`capacity-band-weight-${index}`}
          inputMode="decimal"
          value={draft.bands[index].maxWeightG}
          onChange={(event) => setBand(index, (band) => ({ ...band, maxWeightG: event.target.value }))}
          disabled={disabled}
          style={inputStyle}
        />
        g
      </label>
    )
  }

  const temperatureHeader = (column: number): React.ReactNode => {
    if (column === draft.temperatures.length) {
      return draft.temperatures.length === 0
        ? t('capacity.flow.anyTemperature')
        : t('capacity.flow.moreThan', { temperature: draft.temperatures[column - 1].trim() || '…' })
    }
    return (
      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        {t('editFarm.density.upTo')}
        <input
          aria-label={t('capacity.flow.upToTemperature')}
          data-testid={`capacity-temperature-${column}`}
          inputMode="decimal"
          value={draft.temperatures[column]}
          onChange={(event) => onChange((current) => ({ ...current, temperatures: current.temperatures.map((value, i) => (i === column ? event.target.value : value)) }))}
          disabled={disabled}
          style={{ ...inputStyle, width: 56 }}
        />
        °C
        <button
          type="button"
          aria-label={t('editFarm.density.removeBand')}
          onClick={() => removeTemperature(column)}
          disabled={disabled}
          style={{ border: 'none', background: 'none', color: '#64748b', cursor: 'pointer', padding: 0 }}
        >
          ×
        </button>
      </span>
    )
  }

  return (
    <div style={{ display: 'grid', gap: 16 }} data-testid="capacity-editor">
      <label style={{ display: 'grid', gap: 4, maxWidth: 360 }}>
        <span style={{ fontWeight: 600, color: '#475569' }}>{t('capacity.field.name')}</span>
        <input
          data-testid="capacity-name"
          value={draft.name}
          onChange={(event) => onChange((current) => ({ ...current, name: event.target.value }))}
          disabled={disabled}
          placeholder={t('capacity.field.namePlaceholder')}
          style={{ ...inputStyle, width: '100%' }}
        />
      </label>

      <section style={{ display: 'grid', gap: 8 }}>
        <strong>{t('capacity.density.title')}</strong>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...cellStyle, textAlign: 'left' }}>{t('editFarm.density.weightBand')}</th>
                {UNIT_TYPES.map((type) => <th key={type} style={{ ...cellStyle, textAlign: 'left' }}>{t(UNIT_TYPE_LABEL_KEY[type])}</th>)}
                <th />
              </tr>
            </thead>
            <tbody>
              {draft.bands.map((band, index) => (
                <tr key={index} data-testid={`capacity-density-band-${index}`}>
                  <td style={cellStyle}>{weightCell(index)}</td>
                  {UNIT_TYPES.map((type) => (
                    <td key={type} style={cellStyle}>
                      <input
                        aria-label={`${t(UNIT_TYPE_LABEL_KEY[type])} kg/m³`}
                        data-testid={`capacity-density-${index}-${type}`}
                        inputMode="decimal"
                        value={band.limits[type]}
                        onChange={(event) => setBand(index, (current) => ({ ...current, limits: { ...current.limits, [type]: event.target.value } }))}
                        disabled={disabled}
                        style={inputStyle}
                      />
                    </td>
                  ))}
                  <td style={cellStyle}>
                    {index === draft.bands.length - 1 ? null : (
                      <GradientButton size="xs" variant="secondary" onClick={() => removeBand(index)} disabled={disabled}>
                        {t('editFarm.density.removeBand')}
                      </GradientButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <GradientButton size="xs" variant="secondary" onClick={addBand} disabled={disabled} data-testid="capacity-add-band">
            {t('editFarm.density.addBand')}
          </GradientButton>
        </div>
        <span style={{ fontSize: 12, color: '#6b7280' }}>{t('capacity.density.help')}</span>
      </section>

      <section style={{ display: 'grid', gap: 8 }}>
        <strong>{t('capacity.flow.title')}</strong>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...cellStyle, textAlign: 'left' }}>{t('editFarm.density.weightBand')}</th>
                {Array.from({ length: columns }, (_, column) => (
                  <th key={column} style={{ ...cellStyle, textAlign: 'left' }}>{temperatureHeader(column)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {draft.bands.map((band, index) => (
                <tr key={index} data-testid={`capacity-flow-band-${index}`}>
                  <td style={cellStyle}>
                    {index === draft.bands.length - 1
                      ? weightLabel(index)
                      : t('capacity.flow.weightUpTo', { weight: band.maxWeightG.trim() || '…' })}
                  </td>
                  {Array.from({ length: columns }, (_, column) => (
                    <td key={column} style={cellStyle}>
                      <input
                        aria-label={t('capacity.flow.cellLabel')}
                        data-testid={`capacity-flow-${index}-${column}`}
                        inputMode="decimal"
                        value={band.flow[column] ?? ''}
                        onChange={(event) => setBand(index, (current) => ({ ...current, flow: current.flow.map((value, i) => (i === column ? event.target.value : value)) }))}
                        disabled={disabled}
                        style={inputStyle}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <GradientButton size="xs" variant="secondary" onClick={addTemperature} disabled={disabled} data-testid="capacity-add-temperature">
            {t('capacity.flow.addTemperature')}
          </GradientButton>
        </div>
        <span style={{ fontSize: 12, color: '#6b7280' }}>{t('capacity.flow.help')}</span>
      </section>

      <section style={{ display: 'grid', gap: 8 }}>
        <strong>{t('capacity.hatchery.title')}</strong>
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input
            aria-label={t('capacity.hatchery.title')}
            data-testid="capacity-hatchery-min"
            inputMode="decimal"
            value={draft.hatcheryMin}
            onChange={(event) => onChange((current) => ({ ...current, hatcheryMin: event.target.value }))}
            disabled={disabled}
            style={inputStyle}
          />
          L/min
        </label>
        <span style={{ fontSize: 12, color: '#6b7280' }}>{t('capacity.hatchery.help')}</span>
      </section>
    </div>
  )
}
